import { ElementRef, inject, Injectable, Signal, WritableSignal } from '@angular/core';
import { ExportDialogComponent } from '../dialogs/export/export-dialog/export-dialog.component';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { Table } from '@cumlaude/metadata';
import { HttpClient } from '@angular/common/http';
import { LegendaEntry, LegendaStyle } from '../legenda/legenda.component';
import { ENV_CONFIG, EnvConfiguration } from '@cumlaude/shared-configuration';
import { Dialog } from '@angular/cdk/dialog';
import { ConnectedPosition, Overlay } from '@angular/cdk/overlay';
import { CategoryField } from './metadata';
import { AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { ExportablePanel } from './exportable';

export interface DataExport {
	naam?: string;
	attrs: CategoryField[];
}

export type CustomExportItem = {
	label: string;
	condition: WritableSignal<boolean>;
	component: Signal<ExportablePanel>;
	/** @returns false als dit export-item niet disabled is, anders een string met de reden */
	disabled?: (currentOptions: ExportOptions) => false | string;
};

export interface ExportOptions {
	type: ExportType;
	dataExports: DataExport[];
	selectedColumns: CategoryField[];
	orientation: Orientation;
	showTitle: boolean;
	showFilters: boolean;
	showLegenda: boolean;
	showDatum: boolean;
	customExportItems: CustomExportItem[];
	// De volgende properties zijn alleen voor doorgeven, niet om te veranderen door de gebruiker:
	factTable?: Table;
	attOverrides?: AttOverrides;
	exportTypes: ExportType[];
	legendaStyle?: LegendaStyle;
	legendaEntries?: LegendaEntry[];
}

export enum Orientation {
	PORTRAIT = 'Staand',
	LANDSCAPE = 'Liggend',
}

export enum ExportType {
	AFBEELDING = 'Afbeelding',
	PDF = 'PDF',
	TABEL = 'Tabel',
	DATA = 'Data',
}

/** Pop-out verschijnt met een schone marge onder de knop, niet erover heen. */
const ONDER_KNOP_POSITIES: ConnectedPosition[] = [
	{ originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
	{ originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -8 },
];

@Injectable({
	providedIn: 'root',
})
export class ExportService {
	private readonly dialog = inject(Dialog);
	private readonly overlay = inject(Overlay);
	private readonly http = inject(HttpClient);
	private readonly envConfig = inject<EnvConfiguration>(ENV_CONFIG);

	savedExportOptions: ExportOptions = {
		type: ExportType.AFBEELDING,
		orientation: Orientation.PORTRAIT,
		showTitle: true,
		showFilters: true,
		showLegenda: true,
		showDatum: true,
		customExportItems: [],
		dataExports: [],
		selectedColumns: [],
		exportTypes: Object.values(ExportType),
	};

	/**
	 * emit wanneer de gebruiker de export-dialog met bevestigen heeft afgesloten
	 *
	 * `triggerElement` verankert de dialog onder de knop die hem opent (nieuwe verticale navigatie);
	 * zonder trigger (de oude horizontale `user-tab-menu`) valt terug op de vaste positie rechtsboven.
	 */
	startExport(
		triggerElement: ElementRef | undefined,
		factTable: Table,
		attOverrides: AttOverrides = {},
		exportTypes: ExportType[] = Object.values(ExportType),
		legendaEntries?: LegendaEntry[],
		customExportItems: CustomExportItem[] = []
	): Observable<ExportOptions> {
		const newExportOptions = { ...this.savedExportOptions, factTable, attOverrides, exportTypes, legendaEntries, customExportItems };
		const positionStrategy = triggerElement
			? this.overlay.position().flexibleConnectedTo(triggerElement).withPositions(ONDER_KNOP_POSITIES)
			: this.overlay.position().global().top('7rem').right('1rem');
		const dialogRef = this.dialog.open<ExportOptions>(ExportDialogComponent, {
			data: newExportOptions,
			positionStrategy,
		});
		return dialogRef.closed.pipe(
			filter(Boolean),
			tap(
				(options) =>
					(this.savedExportOptions = {
						...options,
						dataExports: [],
						selectedColumns: options.dataExports.find((dataExport) => !dataExport.naam)?.attrs ?? [],
					})
			)
		);
	}

	exportToPdf(html: string): Observable<Blob> {
		return this.http.post(this.envConfig.restUrl + '/pdf', html, {
			headers: { Accept: 'application/pdf', 'Content-Type': 'text/html' },
			responseType: 'blob',
		});
	}
}
